<template>
  <div>
    <load-profile />
    <!-- Search Container Card -->
    <n-search-container
      v-if="searchFields.length"
      v-model="params"
      :active="true"
      :show-header="false"
      show-search
      :loading="loading"
      @search="search"
      @reset="reset"
    >
      <n-search-input
        ref="search"
        v-model="params"
        :fields="searchFields"
      >
        <template #period="item">
          <b-form-group
            :label-for="item.field.key"
            :label="$t(item.field.label)"
          >
            <b-form-radio-group
              v-if="item.field.type === 'radio'"
              :id="item.field.key"
              :ref="item.field.key"
              v-model="params[item.field.key]"
              buttons
              button-variant="outline-primary rounded-0"
              class="flex-wrap"
            >
              <b-form-radio
                v-for="option in item.field.options"
                :key="option.value"
                :value="option.value"
              >{{ $t(option.text) }}
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </template>
        <template #typeId="item">
          <b-form-group
            :label-for="item.field.key"
            :label="$t(item.field.label)"
          >
            <b-form-radio-group
              v-if="item.field.type === 'radio'"
              :id="item.field.key"
              :ref="item.field.key"
              v-model="params[item.field.key]"
              buttons
              button-variant="outline-primary"
            >
              <b-form-radio
                v-for="option in item.field.options"
                :key="option.value"
                :value="option.value"
              >{{ $t(option.text) }}
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </template>
      </n-search-input>
    </n-search-container>

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-0-5">
        <b-row>
          <b-col>
            <label>{{ $t("general.entries") }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            />
          </b-col>
        </b-row>
      </div>
      <div>
        <n-table
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          @sort-changed="sortChanged"
        >
          <template #cell(username)="{ item }">
            {{ item.username }}
            <small
              v-if="item.secondUsername"
              class="d-block text-wala"
            >
              ({{ item.secondUsername }})
            </small>
          </template>
          <template #cell(amount)="data">
            <span class="text-wala">
              {{ data.item.amount | currency(data.item.ccy) }}
            </span>
          </template>
          <template #cell(imageUrl)="data">
            <a
              v-if="data.item.imageUrl"
              :href="data.item.imageUrl"
              target="_blank"
            >
              <b-img
                :src="data.item.imageUrl"
                fluid
                style="height: 50px"
              />
            </a>
          </template>
          <template #cell(createdAt)="data">
            {{ data.item.createdAt | formatDate }}
          </template>
          <template #cell(status)="data">
            <span
              v-if="data.item.status === '1'"
              class="badge badge-pill badge-success"
            >{{ $t("status.approved") }}</span>
            <span
              v-else-if="data.item.status === '2'"
              class="badge badge-pill badge-danger"
            >{{ $t("status.rejected") }}</span>
            <span
              v-else
              class="badge badge-pill badge-warning"
            >{{
              $t("status.pending")
            }}</span>
          </template>
          <template #cell(actions)="data">
            <b-button
              v-if="data.item.status === '0' && $can('update', 'request-deposit')"
              v-b-tooltip.hover
              :title="$t('button.approve')"
              variant="success"
              pill
              size="sm"
              @click="approveConfirm(data.item)"
            >
              <feather-icon icon="CheckIcon" />
            </b-button>
            <b-button
              v-if="data.item.status === '0' && $can('update', 'request-deposit')"
              v-b-tooltip.hover
              :title="$t('button.reject')"
              variant="danger"
              pill
              size="sm"
              @click="rejectConfirm(data.item)"
            >
              <feather-icon icon="XIcon" />
            </b-button>
          </template>
          <template #custom-foot>
            <b-tr v-if="!loading && totalReport.total">
              <b-th colspan="3" />
              <b-th class="text-right">
                <h5 class="text-wala font-weight-bold">
                  {{ totalReport.total | currency(totalReport.ccy) }}
                </h5>
              </b-th>
              <b-th colspan="5" />
            </b-tr>
          </template>
        </n-table>
        <n-pagination
          ref="pagination"
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="params.page"
          @change="list"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BFormRadioGroup,
  BFormRadio,
  BImg,
  BTr,
  BTh,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Repository from '@/repositories/RepositoryFactory'
import NPagination from '@/components/NPagination.vue'
import NSearchContainer from '@/components/NSearchContainer.vue'
import NSearchInput from '@/components/NSearchInput.vue'
import NTable from '@/components/NTable.vue'
import { avatarText } from '@core/utils/filter'
import { calculateDatesByPeriod } from '@/libs/helper'
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment'
import requestDepositVMenu from '@/navigation/vertical/menu/requestDeposit'
import requestDepositHMenu from '@/navigation/horizontal/menu/requestDeposit'
import requestWithdrawVMenu from '@/navigation/vertical/menu/requestWithdraw'
import requestWithdrawHMenu from '@/navigation/horizontal/menu/requestWithdraw'
import tableFields from './tableField'
import searchInputs from './searchInput'

const DepositRequestRepository = Repository.get('depositRequest')

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormRadioGroup,
    BFormRadio,
    BImg,
    BTr,
    BTh,
    NPagination,
    NSearchContainer,
    NSearchInput,
    NTable,
    vSelect,
  },

  data() {
    return {
      items: [],
      loading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      params: {
        page: Number(this.$route.query.page) || 1,
        order: this.$route.query.order || null,
        sort: this.$route.query.sort || null,
        userId: Number(this.$route.query.userId) || null,
        status: '0',
        startDate: this.$route.query.startDate || moment().format('YYYY-MM-DD'),
        endDate: this.$route.query.endDate || moment().format('YYYY-MM-DD'),
        period: this.$route.query.period || 'today',
        search: this.$route.query.search || null,
      },
      avatarText,
      searchFields: [],
      totalReport: {},
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.state.pagination.perPage
      },
      set(value) {
        this.$store.commit('pagination/SET_PER_PAGE', value)
      },
    },
    activeSearch() {
      return (
        !!this.$route.query.userId
        || !!this.$route.query.startDate
        || !!this.$route.query.endDate
      )
    },
    typeId() {
      if (this.$store.state.profile.typeId) {
        return this.$store.state.profile.typeId
      }
      return this.userData?.id
    },
  },
  watch: {
    perPage() {
      this.list(1)
    },
    'params.period': function (value) {
      const dates = calculateDatesByPeriod(value)
      if (dates.length) {
        this.params.startDate = dates[0]
        this.params.endDate = dates[1]
      }
    },
    typeId() {
      this.setSearchFields(this.typeId)
    },
  },
  mounted() {
    this.getData(this.params.page)
    if (this.$store.state.profile.typeId) {
      this.setSearchFields()
    }
  },
  methods: {
    setSearchFields() {
      this.searchFields = [...searchInputs]
    },
    updateQuerySting() {
      this.$router.replace({ query: this.params })
    },
    list(page = 1) {
      this.params.page = page
      this.updateQuerySting()
      this.getData()
    },
    sortChanged(sortBy, sortDirection) {
      this.params.order = sortBy
      this.params.sort = sortDirection
      this.updateQuerySting()
      this.getData()
    },
    search(searchText) {
      this.params.search = searchText
      this.params.page = 1
      this.$refs.pagination.reset()
      this.updateQuerySting()
      this.getData()
    },
    reset() {
      this.$refs.search.reset()
      this.params.page = 1
      this.$refs.pagination.reset()
      this.params.startDate = moment().format('YYYY-MM-DD')
      this.params.toDate = moment().format('YYYY-MM-DD')
      this.params.period = 'today'
      this.params.search = ''
      this.updateQuerySting()
      this.getData()
    },
    getData() {
      this.loading = true
      DepositRequestRepository.index({
        ...this.params,
      })
        .then(response => {
          const data = response.data.data.list
          this.items = [...data]
          this.total = response.data.data.total
          this.loading = false
          this.getTotal()
        })
        .catch(() => {
          this.loading = false
        })
    },
    getTotal() {
      DepositRequestRepository.total({
        ...this.params,
      })
        .then(response => {
          const { data } = response.data
          this.totalReport = {
            ...data,
          }
        })
        .catch(() => {})
    },
    approveConfirm(item) {
      this.$bvModal
        .msgBoxConfirm(this.$t('alert.unableToRevertAfterSave'), {
          title: this.$t('alert.areYouSure'),
          size: 'sm',
          buttonSize: 'md',
          okTitle: this.$t('button.approve'),
          cancelTitle: this.$t('button.cancel'),
          cancelVariant: 'outline-secondary',
          okVariant: 'success',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            this.approveReject(item, 1)
          }
        })
    },
    rejectConfirm(item) {
      this.$bvModal
        .msgBoxConfirm(this.$t('alert.unableToRevertAfterSave'), {
          title: this.$t('alert.areYouSure'),
          size: 'sm',
          buttonSize: 'md',
          okTitle: this.$t('button.reject'),
          cancelTitle: this.$t('button.cancel'),
          cancelVariant: 'outline-secondary',
          okVariant: 'danger',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            this.approveReject(item, 2)
          }
        })
    },
    approveReject(item, status) {
      // Status 1: Approve; 2: Reject
      const data = {
        trxId: item.id,
        status,
      }
      DepositRequestRepository.update(data)
        .then(() => {
          this.getData()
          this.count()
        })
        .then(() => {
          this.loading = false
        })
    },
    count() {
      DepositRequestRepository.count().then(response => {
        const data = response.data?.data
        this.setBadge(data)
      })
    },
    setBadge(data) {
      if (requestDepositVMenu.length) {
        requestDepositVMenu[0].tag = data.deposit
      }
      if (requestWithdrawVMenu.length) {
        requestWithdrawVMenu[0].tag = data.withdrawal
      }
      if (requestDepositHMenu.length) {
        requestDepositHMenu[0].tag = data.deposit
      }
      if (requestWithdrawHMenu.length) {
        requestWithdrawHMenu[0].tag = data.withdrawal
      }
      this.$store.commit('profile/SET_BADGE', data)
    },
  },
  setup() {
    const fields = [...tableFields]
    return {
      fields,
    }
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
